import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

interface Option {
    id: string;
    name: string;
}

interface MultiSelectDropdownProps {
    label: string;
    icon: React.ReactNode;
    options: Option[];
    selectedValues: string[];
    onSelectionChange: (selectedValues: string[]) => void;
    placeholder?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    icon,
    options,
    selectedValues,
    onSelectionChange,
    placeholder = "Select options..."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggleOption = (optionId: string) => {
        if (selectedValues.includes(optionId)) {
            onSelectionChange(selectedValues.filter(id => id !== optionId));
        } else {
            onSelectionChange([...selectedValues, optionId]);
        }
    };

    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectionChange([]);
    };

    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return placeholder;
        }
        if (selectedValues.length === 1) {
            const option = options.find(opt => opt.id === selectedValues[0]);
            return option ? option.name : placeholder;
        }
        return `${selectedValues.length} selected`;
    };

    const selectedOptions = options.filter(option => selectedValues.includes(option.id));

    return (
        <div className="space-y-2">
            <label className="flex text-sm font-medium text-slate-700 items-center gap-2">
                {icon}
                {label}
            </label>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full px-3 py-2 text-left border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
                >
                    <span className={`truncate ${selectedValues.length === 0 ? 'text-slate-500' : 'text-slate-700'}`}>
                        {getDisplayText()}
                    </span>
                    <div className="flex items-center gap-2">
                        {selectedValues.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="p-1 hover:bg-slate-200 rounded-full transition-colors"
                                title="Clear all"
                            >
                                <X className="w-3 h-3 text-slate-500" />
                            </button>
                        )}
                        <ChevronDown
                            className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2">
                            {options.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-slate-500 text-center">
                                    No options available
                                </div>
                            ) : (
                                options.map((option) => {
                                    const isSelected = selectedValues.includes(option.id);
                                    return (
                                        <label
                                            key={option.id}
                                            className="flex items-center px-3 py-2 hover:bg-slate-50 rounded-md cursor-pointer transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleOption(option.id)}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 border-2 rounded flex items-center justify-center mr-3 transition-colors ${isSelected
                                                ? 'bg-blue-600 border-blue-600'
                                                : 'border-slate-300 hover:border-blue-400'
                                                }`}>
                                                {isSelected && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <span className="text-sm text-slate-700 flex-1">{option.name}</span>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Selected items display */}
            {selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {selectedOptions.map((option) => (
                        <span
                            key={option.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                        >
                            {option.name}
                            <button
                                onClick={() => handleToggleOption(option.id)}
                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;




